import apiClient from '@/utils/apiClient';
import {
	ProvisionedNumberResponse,
	DisabledAutoReplyResponse,
	ContactsResponse,
	BulkAIAutoReplyRequest,
	UpdateContactRequest,
	GetContactsRequest,
	GetProvisionedNumberRequest,
	GetDisabledAutoReplySettingsRequest,
} from '@/interfaces/ITextingPlatform';
import { ContactData } from '@/types/TTextingPlatform';
import { safeJSONParse } from '@/utils/helper';

const GET_PROVISIONED_NUMBER_API_PATH = '/get-provisioned-number';
const GET_TEXTING_CONVOS_API_PATH = (agentId: string) => `/agent/${agentId}/get-texting-convos`;
const GET_CONTACTS_API_PATH = '/agent/get-contacts';
const GET_DISABLED_AUTOREPLY_SETTINGS_API_PATH = '/agent/get-disabled-autoreply-settings';
const UPDATE_CONTACT_API_PATH = '/agent/update-contact';
const UPDATE_AI_AUTO_REPLYS_API_PATH = '/agent/update-ai-auto-replys';
const SEND_MANUAL_SMS_API_PATH = '/agent/send-manual-sms';
const UPDATE_AI_AUTO_REPLY_API_PATH = '/agent/update-ai-auto-reply';
const SEND_MANUAL_SMS_BLAST_API_PATH = '/agent/send-manual-sms-blast';
const UPDATE_CONTACTS_API_PATH = '/agent/update-contacts';

export const getContacts = async (username: string): Promise<ContactData> => {
	const response: ContactsResponse = await apiClient.request({
		method: 'POST',
		endPoint: GET_CONTACTS_API_PATH,
		body: { username } as GetContactsRequest,
		clientType: 'RECEPTIONIST',
	});

	return response.result;
};

export const getProvisionedNumber = async (username: string): Promise<string | null> => {
	const response: ProvisionedNumberResponse = await apiClient.request({
		method: 'POST',
		endPoint: GET_PROVISIONED_NUMBER_API_PATH,
		body: { username } as GetProvisionedNumberRequest,
		clientType: 'RECEPTIONIST',
	});

	if (response.status === 'success' && response.provisionedNumber) {
		return response.provisionedNumber;
	}
	return null;
};

export const getTextingConversations = async (
	selectedAgentId: string,
	provisionedNumber: string,
	type: 'initial' | 'more_conversations' | 'more_messages' = 'initial',
	cursor?: string,
) => {
	const body: any = { provisionedNumber, type };

	if (cursor) {
		body.cursor = cursor;
	}

	const response = await apiClient.request({
		method: 'POST',
		endPoint: GET_TEXTING_CONVOS_API_PATH(selectedAgentId),
		body,
		clientType: 'RECEPTIONIST',
	});

	if (response.status === 'success') {
		return {
			conversations: response.conversations || {},
			hasMore: response.hasMore || false,
			nextCursor: response.nextCursor || null,
			type: response.type || 'conversations',
			messages: response.messages || null,
			conversationNumber: response.conversationNumber || null,
		};
	}

	return type === 'initial'
		? {}
		: {
				conversations: {},
				hasMore: false,
				nextCursor: null,
				type: 'conversations',
				messages: null,
				conversationNumber: null,
			};
};

export const getDisabledAutoReplySettings = async (username: string): Promise<string[]> => {
	const response: DisabledAutoReplyResponse = await apiClient.request({
		method: 'POST',
		endPoint: GET_DISABLED_AUTOREPLY_SETTINGS_API_PATH,
		body: { username } as GetDisabledAutoReplySettingsRequest,
		clientType: 'RECEPTIONIST',
	});

	if (response.status === 'success' && response.dontAutoReplyToTheseNumbers) {
		return safeJSONParse(response.dontAutoReplyToTheseNumbers, []);
	}

	return [];
};

export const updateContact = async (
	contactAddingNumber: string,
	createContactVal: string,
	username: string,
): Promise<void> => {
	await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_CONTACT_API_PATH,
		body: {
			contact: { [contactAddingNumber]: createContactVal },
			username,
		} as UpdateContactRequest,
		clientType: 'RECEPTIONIST',
	});
};

export const updateBulkAIAutoReply = async (request: BulkAIAutoReplyRequest): Promise<void> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_AI_AUTO_REPLYS_API_PATH,
		body: request,
		clientType: 'RECEPTIONIST',
	});

	if (response.status !== 'success') {
		throw new Error('Failed to update AI Autoreply Settings');
	}
};

export const sendManualSMS = async (request: {
	message: string;
	from: string;
	theirNumber: string;
	to: string;
}): Promise<void> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: SEND_MANUAL_SMS_API_PATH,
		body: request,
		clientType: 'RECEPTIONIST',
	});

	if (response.status !== 'success') {
		throw new Error('Failed to send message');
	}
};

export const updateAIAutoReply = async (request: {
	toNumber: string;
	disableAiAutoReply: boolean;
	username: string;
}): Promise<void> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_AI_AUTO_REPLY_API_PATH,
		body: request,
		clientType: 'RECEPTIONIST',
	});

	if (response.status !== 'success') {
		throw new Error('Failed to update AI AutoReply settings');
	}
};

export const sendBulkSMS = async (request: {
	messages: string[];
	from: string;
	theirNumber: string;
	recepients: string[];
}): Promise<void> => {
	const response = await apiClient.request({
		method: 'POST',
		endPoint: SEND_MANUAL_SMS_BLAST_API_PATH,
		body: request,
		clientType: 'RECEPTIONIST',
	});

	if (response.status !== 'success') {
		throw new Error('Failed to send messages');
	}
};

export const updateContacts = async (request: {
	mapping: { [key: string]: string };
	username: string;
}): Promise<void> => {
	await apiClient.request({
		method: 'POST',
		endPoint: UPDATE_CONTACTS_API_PATH,
		body: request,
		clientType: 'RECEPTIONIST',
	});
};
